import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Checkbox,
  Divider,
  InlineStack,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import type {
  WheelConfigV1,
  WheelInteractionV1,
  WheelLeadField,
  WheelRewardType,
  WheelSegmentV1,
  WheelSpinResponse,
} from "../../types/wheel";
import {
  getSpinableSegments,
  isWheelRewardType,
  WHEEL_REWARD_TYPES,
  withWheelEngineDefaults,
} from "../../types/wheel";
import {
  fetchWheelVariantStats,
  spinWheelPreview,
  updateWheelConfig,
  type WheelVariantStats,
} from "../../api/campaigns";
import { isShopInstallRedirectError } from "../../api/admin-api-client";
import { SimpleWheel } from "./SimpleWheel";
import { segmentCenterDegreesFromTop } from "./wheelGeometry";

function rewardTypeLabel(t: WheelRewardType): string {
  switch (t) {
    case "message":
      return "Message";
    case "discount_percent":
      return "Discount %";
    case "discount_code":
      return "Discount code";
    case "points":
      return "Loyalty points";
    case "free_shipping":
      return "Free shipping";
  }
}

function defaultRewardValueFor(type: WheelRewardType): string | number {
  switch (type) {
    case "message":
    case "free_shipping":
      return "";
    case "discount_code":
      return "SAVE10";
    case "discount_percent":
      return 10;
    case "points":
      return 0;
  }
}

function coerceRewardType(raw: unknown): WheelRewardType {
  if (raw === "discount") return "discount_percent";
  if (isWheelRewardType(raw)) return raw;
  return "message";
}

function coerceRewardValue(raw: unknown, rewardType: WheelRewardType): string | number {
  switch (rewardType) {
    case "message":
    case "free_shipping":
    case "discount_code":
      return raw == null ? "" : String(raw);
    case "discount_percent": {
      if (typeof raw === "number" && Number.isFinite(raw)) {
        return Math.min(100, Math.max(0, raw));
      }
      const n = parseFloat(String(raw));
      return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 10;
    }
    case "points": {
      if (typeof raw === "number" && Number.isFinite(raw)) {
        return Math.max(0, Math.round(raw));
      }
      const n = parseInt(String(raw), 10);
      return Number.isFinite(n) ? Math.max(0, n) : 0;
    }
  }
}

function normalizeInteraction(raw: unknown): WheelInteractionV1 | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as { type?: unknown; requiredFields?: unknown };
  if (r.type === "instant") return { type: "instant" };
  if (r.type === "instant_discount") return { type: "instant_discount" };
  if (r.type === "lead_capture") {
    const fields = Array.isArray(r.requiredFields)
      ? r.requiredFields.filter((x): x is WheelLeadField => x === "email" || x === "phone")
      : [];
    return {
      type: "lead_capture",
      requiredFields: fields.length > 0 ? fields : ["email"],
    };
  }
  return undefined;
}

function defaultWheelConfig(): WheelConfigV1 {
  return withWheelEngineDefaults({
    segments: [
      {
        id: crypto.randomUUID(),
        label: "10% off",
        weight: 1,
        rewardType: "discount_percent",
        rewardValue: 10,
        enabled: true,
      },
      {
        id: crypto.randomUUID(),
        label: "Try again",
        weight: 1,
        rewardType: "message",
        rewardValue: "Better luck next time",
        enabled: true,
      },
    ],
  });
}

type LeadFieldsChoice = "email" | "phone" | "both";

function leadFieldsToChoice(fields: WheelLeadField[] | undefined): LeadFieldsChoice {
  const f = fields?.length ? fields : (["email"] as WheelLeadField[]);
  const hasE = f.includes("email");
  const hasP = f.includes("phone");
  if (hasE && hasP) return "both";
  if (hasP) return "phone";
  return "email";
}

function choiceToLeadFields(choice: LeadFieldsChoice): WheelLeadField[] {
  if (choice === "both") return ["email", "phone"];
  if (choice === "phone") return ["phone"];
  return ["email"];
}

function normalizeWheelConfig(raw: unknown): WheelConfigV1 {
  const w = raw as Partial<WheelConfigV1> | null | undefined;
  if (w?.segments && Array.isArray(w.segments) && w.segments.length >= 2) {
    return withWheelEngineDefaults({
      segments: w.segments.map((s) => {
        const rt = coerceRewardType(s.rewardType);
        return {
          id: String(s.id ?? crypto.randomUUID()),
          label: String(s.label ?? ""),
          weight: typeof s.weight === "number" && s.weight > 0 ? s.weight : 1,
          rewardType: rt,
          rewardValue: coerceRewardValue(s.rewardValue, rt),
          enabled: s.enabled !== false,
        };
      }),
      engineVersion: typeof w.engineVersion === "string" ? w.engineVersion : undefined,
      visualVariant: typeof w.visualVariant === "string" ? w.visualVariant : undefined,
      interaction: normalizeInteraction(w?.interaction),
    });
  }
  return defaultWheelConfig();
}

interface SimpleWheelEditorProps {
  variantId: string;
  wheelConfig: unknown;
  onSaved?: () => Promise<void>;
}

export function SimpleWheelEditor({ variantId, wheelConfig, onSaved }: SimpleWheelEditorProps) {
  const [draft, setDraft] = useState<WheelConfigV1>(() => normalizeWheelConfig(wheelConfig));
  const [rotationDeg, setRotationDeg] = useState(0);
  const [lastSpin, setLastSpin] = useState<WheelSpinResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<WheelVariantStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const refreshStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const s = await fetchWheelVariantStats(variantId);
      setStats(s);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [variantId]);

  useEffect(() => {
    setDraft(normalizeWheelConfig(wheelConfig));
  }, [wheelConfig, variantId]);

  useEffect(() => {
    void refreshStats();
  }, [refreshStats]);

  /** Same rule as backend: enabled, weight > 0, finite — original order preserved */
  const spinable = useMemo(() => getSpinableSegments(draft.segments), [draft.segments]);

  const updateSegment = useCallback((index: number, patch: Partial<WheelSegmentV1>) => {
    setDraft((prev) => {
      const segments = prev.segments.map((s, i) => (i === index ? { ...s, ...patch } : s));
      return { ...prev, segments };
    });
  }, []);

  const addSegment = useCallback(() => {
    setDraft((prev) => ({
      ...prev,
      segments: [
        ...prev.segments,
        {
          id: crypto.randomUUID(),
          label: "New prize",
          weight: 1,
          rewardType: "message",
          rewardValue: "Thanks for playing",
          enabled: true,
        },
      ],
    }));
  }, []);

  const removeSegment = useCallback((index: number) => {
    setDraft((prev) => ({
      ...prev,
      segments: prev.segments.filter((_, i) => i !== index),
    }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateWheelConfig(variantId, draft);
      await onSaved?.();
      await refreshStats();
    } catch (e) {
      if (isShopInstallRedirectError(e)) return;
      setError(e instanceof Error ? e.message : "Failed to save wheel");
    } finally {
      setSaving(false);
    }
  };

  const handleSpinPreview = async () => {
    if (spinable.length < 2) {
      setError("Need at least two enabled segments with positive weights.");
      return;
    }
    setSpinning(true);
    setError(null);
    try {
      const result = await spinWheelPreview(variantId);
      const centers = segmentCenterDegreesFromTop(spinable);
      const centerDeg = centers[result.resultIndex];
      if (centerDeg === undefined) {
        setError("Spin result did not match wheel layout");
        return;
      }
      const extraTurns = 360 * (4 + Math.floor(Math.random() * 2));
      const align = (360 - (centerDeg % 360)) % 360;
      setRotationDeg((r) => r + extraTurns + align);
      setLastSpin(result);
    } catch (e) {
      if (isShopInstallRedirectError(e)) return;
      setError(e instanceof Error ? e.message : "Spin failed");
    } finally {
      setSpinning(false);
    }
  };

  const rewardOptions = WHEEL_REWARD_TYPES.map((v) => ({
    label: rewardTypeLabel(v),
    value: v,
  }));

  const interactionMode =
    draft.interaction?.type === "lead_capture"
      ? "lead_capture"
      : draft.interaction?.type === "instant_discount"
        ? "instant_discount"
        : "instant";
  const leadFieldsChoice = leadFieldsToChoice(
    draft.interaction?.type === "lead_capture" ? draft.interaction.requiredFields : undefined
  );

  const setInteractionMode = (mode: "instant" | "lead_capture" | "instant_discount") => {
    if (mode === "instant") {
      setDraft((prev) => ({ ...prev, interaction: { type: "instant" } }));
      return;
    }
    if (mode === "instant_discount") {
      setDraft((prev) =>
        withWheelEngineDefaults({ ...prev, interaction: { type: "instant_discount" } })
      );
      return;
    }
    setDraft((prev) =>
      withWheelEngineDefaults({
        ...prev,
        interaction: {
          type: "lead_capture",
          requiredFields: choiceToLeadFields(
            leadFieldsToChoice(
              prev.interaction?.type === "lead_capture"
                ? prev.interaction.requiredFields
                : undefined
            )
          ),
        },
      })
    );
  };

  const setLeadFieldsChoice = (choice: LeadFieldsChoice) => {
    setDraft((prev) =>
      withWheelEngineDefaults({
        ...prev,
        interaction: {
          type: "lead_capture",
          requiredFields: choiceToLeadFields(choice),
        },
      })
    );
  };

  return (
    <BlockStack gap="400">
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Box padding="300" background="bg-surface-secondary" borderRadius="200">
        <BlockStack gap="200">
          <Text variant="headingSm" as="h3">
            Performance (all time)
          </Text>
          <Text variant="bodySm" tone="subdued" as="p">
            Sessions, spins, and either discount-tagged spins (Instant Discount wheel) or lead metrics
            (Lead capture wheel).
          </Text>
          <InlineStack gap="600" wrap blockAlign="start">
            <BlockStack gap="100">
              <Text variant="bodySm" tone="subdued" as="span">
                Sessions
              </Text>
              <Text variant="headingLg" as="span">
                {statsLoading ? "…" : (stats?.sessions ?? "—")}
              </Text>
            </BlockStack>
            <BlockStack gap="100">
              <Text variant="bodySm" tone="subdued" as="span">
                Spins
              </Text>
              <Text variant="headingLg" as="span">
                {statsLoading ? "…" : (stats?.spins ?? "—")}
              </Text>
            </BlockStack>
            {stats?.interactionType === "instant_discount" && (
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued" as="span">
                  Discount spins (tagged)
                </Text>
                <Text variant="headingLg" as="span">
                  {statsLoading ? "…" : (stats?.discountSpins ?? "—")}
                </Text>
              </BlockStack>
            )}
            <BlockStack gap="100">
              <Text variant="bodySm" tone="subdued" as="span">
                Leads captured
              </Text>
              <Text variant="headingLg" as="span">
                {statsLoading
                  ? "…"
                  : stats?.interactionType === "lead_capture"
                    ? (stats?.leads ?? "—")
                    : "—"}
              </Text>
            </BlockStack>
            <BlockStack gap="100">
              <Text variant="bodySm" tone="subdued" as="span">
                Lead conversion
              </Text>
              <Text variant="headingLg" as="span">
                {statsLoading
                  ? "…"
                  : stats?.interactionType === "lead_capture" && stats?.conversionRate != null
                    ? `${stats.conversionRate}%`
                    : "—"}
              </Text>
            </BlockStack>
          </InlineStack>
        </BlockStack>
      </Box>

      <p style={{ margin: 0, fontSize: "0.8125rem", color: "#6d7175" }}>
        Preview spin uses the last <strong>saved</strong> wheel on the server — not unsaved edits in this form.
        Save before spinning to test your latest changes.
      </p>

      <InlineStack align="center" blockAlign="start" gap="600" wrap>
        <SimpleWheel segments={spinable} rotationDeg={rotationDeg} />
        <div style={{ flex: 1, minWidth: 260 }}>
          <BlockStack gap="300">
            <BlockStack gap="200">
              <p style={{ fontWeight: 600, margin: 0 }}>Spin behavior</p>
              <p style={{ margin: 0, fontSize: "0.8125rem", color: "#6d7175" }}>
                Choose spin behavior: instant engagement, commerce-first discount outcomes, or contact
                required before spin (lead capture).
              </p>
              <Select
                label="Interaction"
                options={[
                  { label: "Instant — spin right away", value: "instant" },
                  {
                    label: "Instant Discount — spin right away, discount-focused rewards",
                    value: "instant_discount",
                  },
                  { label: "Lead capture — contact required before spin", value: "lead_capture" },
                ]}
                value={interactionMode}
                onChange={(v) =>
                  setInteractionMode(v as "instant" | "lead_capture" | "instant_discount")
                }
              />
              {interactionMode === "lead_capture" && (
                <Select
                  label="Required contact fields"
                  options={[
                    { label: "Email only", value: "email" },
                    { label: "Phone only", value: "phone" },
                    { label: "Email and phone", value: "both" },
                  ]}
                  value={leadFieldsChoice}
                  onChange={(v) => setLeadFieldsChoice(v as LeadFieldsChoice)}
                />
              )}
            </BlockStack>

            <Divider />

            <p style={{ fontWeight: 600, margin: 0 }}>Wheel segments</p>
            {draft.segments.map((seg, index) => (
              <div
                key={seg.id}
                style={{
                  border: "1px solid #e3e3e3",
                  borderRadius: 8,
                  padding: "0.75rem",
                }}
              >
                <BlockStack gap="200">
                  <TextField
                    label="Label"
                    value={seg.label}
                    onChange={(v) => updateSegment(index, { label: v })}
                    autoComplete="off"
                  />
                  <TextField
                    label="Weight"
                    type="number"
                    value={String(seg.weight)}
                    onChange={(v) =>
                      updateSegment(index, { weight: Math.max(0.0001, parseFloat(v) || 0) })
                    }
                    autoComplete="off"
                    min={0.0001}
                    step={0.1}
                  />
                  <Select
                    label="Reward type"
                    options={rewardOptions}
                    value={seg.rewardType}
                    onChange={(v) => {
                      const next = v as WheelRewardType;
                      updateSegment(index, {
                        rewardType: next,
                        rewardValue: defaultRewardValueFor(next),
                      });
                    }}
                  />
                  {seg.rewardType === "discount_code" ? (
                    <TextField
                      label="Checkout code"
                      helpText="Customer sees this exact code after winning (e.g. SAVE10)."
                      value={String(seg.rewardValue)}
                      onChange={(v) => updateSegment(index, { rewardValue: v })}
                      autoComplete="off"
                    />
                  ) : seg.rewardType === "message" || seg.rewardType === "free_shipping" ? (
                    <TextField
                      label={
                        seg.rewardType === "message"
                          ? "Message text"
                          : "Note (optional)"
                      }
                      value={String(seg.rewardValue)}
                      onChange={(v) => updateSegment(index, { rewardValue: v })}
                      autoComplete="off"
                      multiline={seg.rewardType === "message" ? 2 : 1}
                    />
                  ) : (
                    <TextField
                      label={seg.rewardType === "discount_percent" ? "Percent (0–100)" : "Points (integer)"}
                      type="number"
                      value={String(seg.rewardValue)}
                      onChange={(v) => {
                        const n = parseFloat(v);
                        if (seg.rewardType === "discount_percent") {
                          updateSegment(index, {
                            rewardValue: Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0,
                          });
                        } else {
                          updateSegment(index, {
                            rewardValue: Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0,
                          });
                        }
                      }}
                      autoComplete="off"
                      min={0}
                      max={seg.rewardType === "discount_percent" ? 100 : undefined}
                    />
                  )}
                  <Checkbox
                    label="Enabled"
                    checked={seg.enabled}
                    onChange={(v) => updateSegment(index, { enabled: v })}
                  />
                  <Button
                    size="slim"
                    tone="critical"
                    onClick={() => removeSegment(index)}
                    disabled={draft.segments.length <= 2}
                  >
                    Remove segment
                  </Button>
                </BlockStack>
              </div>
            ))}
            <InlineStack gap="200">
              <Button onClick={addSegment}>Add segment</Button>
              <Button variant="primary" onClick={handleSave} loading={saving}>
                Save wheel
              </Button>
              <Button onClick={handleSpinPreview} loading={spinning} disabled={spinable.length < 2}>
                Spin (preview)
              </Button>
            </InlineStack>
          </BlockStack>
        </div>
      </InlineStack>

      {lastSpin && (
        <Banner tone="success" onDismiss={() => setLastSpin(null)}>
          Result: {lastSpin.reward.rewardType} — {String(lastSpin.reward.rewardValue)}
          {lastSpin.interaction?.type === "instant_discount" ? " (Instant Discount wheel)" : ""}{" "}
          (segment {lastSpin.segmentId})
        </Banner>
      )}
    </BlockStack>
  );
}
